import { useEffect, useMemo, useCallback } from "react";
import { ExternalLink, Filter, Loader2, RefreshCw, X } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useStateMachine } from "@/core/arch/state_machine";
import { MoneyRepository } from "@/application/money/money_repository";
import { PayoutsListBloc } from "@/application/money/payouts_list_bloc";
import type {
  Payout,
  PayoutFilter,
  PayoutStatus,
} from "@/application/money/types";
import {
  formatRelative,
  formatAbsolute,
  formatShortDate,
  defaultPayoutFilter,
} from "@/application/money/types";
import { payoutUrl } from "@/lib/stripe-links";
import { Button } from "@/components/ui/base/button";
import { Badge } from "@/components/ui/base/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { CursorPagination } from "@/components/ui/data-table/CursorPagination";
import { MoneyStatusBadge } from "./components/MoneyStatusBadge";
import { AmountCell } from "./components/AmountCell";

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS: {
  label: string;
  value: Exclude<PayoutFilter["dateRange"]["preset"], "custom">;
}[] = [
  { label: "Last 1 hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

const PAYOUT_STATUSES: PayoutStatus[] = [
  "pending",
  "in_transit",
  "paid",
  "failed",
  "canceled",
];

// ── Filter toolbar ────────────────────────────────────────────────────────────

interface FilterToolbarProps {
  filters: PayoutFilter;
  onFilterChange: (patch: Partial<PayoutFilter>) => void;
  onReset: () => void;
}

function FilterToolbar({
  filters,
  onFilterChange,
  onReset,
}: FilterToolbarProps) {
  const activeDateLabel =
    filters.dateRange.preset !== "all" && filters.dateRange.preset !== "custom"
      ? (DATE_PRESETS.find((p) => p.value === filters.dateRange.preset)
          ?.label ?? null)
      : null;

  const nonDateCount = filters.statuses.length > 0 ? 1 : 0;
  const isFiltered = nonDateCount > 0 || activeDateLabel !== null;

  function toggleStatus(status: PayoutStatus) {
    const current = filters.statuses;
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    onFilterChange({ statuses: next });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeDateLabel && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1.5 text-[10px] font-normal"
              >
                {activeDateLabel}
              </Badge>
            )}
            {nonDateCount > 0 && (
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                +{nonDateCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex flex-col gap-4">
            {/* Status multi-select */}
            <div className="flex flex-col gap-1.5">
              <span className="text-muted-foreground text-xs font-medium">
                Status
              </span>
              <div className="flex flex-wrap gap-1.5">
                {PAYOUT_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStatus(s)}
                    className={[
                      "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize transition-colors",
                      filters.statuses.includes(s)
                        ? "bg-blue-600 text-white"
                        : "bg-muted text-muted-foreground hover:bg-accent",
                    ].join(" ")}
                  >
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                Date range
              </span>
              <Select
                value={filters.dateRange.preset}
                onValueChange={(v) => {
                  const preset = v as Exclude<
                    PayoutFilter["dateRange"]["preset"],
                    "custom"
                  >;
                  onFilterChange({ dateRange: { preset } });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {isFiltered && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onReset}
          aria-label="Reset filters"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

interface PayoutsListProps {
  businessId: string;
  /**
   * Renders a short, toolbar-less list for an overview section. The full
   * list lives on its own tab, which is where filtering belongs.
   */
  previewLimit?: number;
}

export function PayoutsList({ businessId, previewLimit }: PayoutsListProps) {
  const [state, bloc] = useStateMachine(
    () => new PayoutsListBloc(new MoneyRepository(), businessId),
  );

  useEffect(() => {
    void bloc.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = useCallback(
    (patch: Partial<PayoutFilter>) => {
      void bloc.setFilter(patch);
    },
    [bloc],
  );

  const handleReset = useCallback(() => {
    void bloc.setFilter(defaultPayoutFilter());
  }, [bloc]);

  const columns: ColumnDef<Payout, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => {
          const created = row.getValue("created") as number | null;
          if (created == null)
            return <span className="text-subtle-foreground">—</span>;
          return (
            <span
              className="text-muted-foreground text-sm whitespace-nowrap"
              title={formatAbsolute(created)}
            >
              {formatRelative(created)}
            </span>
          );
        },
      },
      {
        accessorKey: "arrivalDate",
        header: "Arrives",
        cell: ({ row }) => {
          const arrivalDate = row.getValue("arrivalDate") as number | null;
          if (arrivalDate == null)
            return <span className="text-subtle-foreground">—</span>;
          return (
            <span className="text-muted-foreground text-sm whitespace-nowrap">
              {formatShortDate(arrivalDate)}
            </span>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <AmountCell
            cents={row.getValue("amount") as number | null}
            currency={row.original.currency}
          />
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const payout = row.original;
          const status = row.getValue("status") as string | null;
          return (
            <div className="flex flex-col items-start gap-0.5">
              {status ? (
                <MoneyStatusBadge status={status} />
              ) : (
                <span className="text-subtle-foreground">—</span>
              )}
              {status === "failed" &&
                (payout.failureCode ?? payout.failureMessage) && (
                  <span className="text-subtle-foreground text-xs">
                    {payout.failureCode && (
                      <span className="font-mono">{payout.failureCode}</span>
                    )}
                    {payout.failureCode && payout.failureMessage && " · "}
                    {payout.failureMessage && (
                      <span>&ldquo;{payout.failureMessage}&rdquo;</span>
                    )}
                  </span>
                )}
            </div>
          );
        },
      },
      {
        id: "method",
        header: "Method",
        cell: ({ row }) => {
          const payout = row.original;
          const parts = [payout.method, payout.type]
            .filter(Boolean)
            .join(" · ");
          return (
            <span className="text-muted-foreground text-xs">
              {parts || "—"}
            </span>
          );
        },
      },
      {
        id: "stripe_link",
        header: "",
        cell: ({ row }) => {
          const payout = row.original;
          if (!payout.sourceId || !payout.sourceAccount) return null;
          const url = payoutUrl(payout.sourceId, payout.sourceAccount, {
            currency: payout.currency,
          });
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="View in Stripe dashboard"
              className="text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          );
        },
      },
    ],
    [],
  );

  const renderToolbar = () =>
    previewLimit ? null : (
      <FilterToolbar
        filters={state.filters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />
    );

  if (state.status === "loading") {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">
          Failed to load payouts: {state.error}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void bloc.load()}
          className="gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const hasItems = state.items.length > 0;
  const isFiltering =
    state.filters.statuses.length > 0 ||
    state.filters.dateRange.preset !== "all";

  if (state.status === "loaded" && !hasItems && !isFiltering) {
    return (
      <div className="flex flex-col gap-4">
        {renderToolbar()}
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-sm">
            This merchant hasn&apos;t received any payouts yet.
          </p>
        </div>
      </div>
    );
  }

  if (state.status === "loaded" && !hasItems && isFiltering) {
    return (
      <div className="flex flex-col gap-4">
        {renderToolbar()}
        <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
          <p className="text-sm">No payouts match these filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Rows are not clickable — no onRowClick prop */}
      <DataTable
        columns={columns}
        data={previewLimit ? state.items.slice(0, previewLimit) : state.items}
        toolbar={renderToolbar}
        footer={
          <CursorPagination
            hasPrev={state.hasPrev}
            hasNext={state.hasNext}
            onPrev={() => void bloc.prevPage()}
            onNext={() => void bloc.nextPage()}
            // CursorPagination only renders post-load guard; buttons can't be clicked mid-fetch
            loading={false}
          />
        }
      />
    </div>
  );
}
